import { useState } from 'react';
import { Icon } from '@atoms/index';
import type { Restaurant, Promotion } from '@/types';
import { formatRating, formatPrice } from '@/utils/format';
import { cn } from '@utils/cn';

interface RestaurantCardProps {
  restaurant: Restaurant;
  onClick?: () => void;
}

const getPromotionBadgeText = (promotion: Promotion): string => {
  switch (promotion.type) {
    case 'FREE_DELIVERY':
      return 'Ücretsiz Teslimat';
    case 'DISCOUNT_PERCENTAGE':
      return `%${promotion.discountValue} İndirim`;
    case 'DISCOUNT_AMOUNT':
      return `${promotion.discountValue}₺ İndirim`;
    default:
      return promotion.name;
  }
};

const RestaurantCard = ({ restaurant, onClick }: RestaurantCardProps) => {
  const [bannerSrc, setBannerSrc] = useState<string | null>(
    restaurant.bannerUrl || restaurant.logoUrl || null,
  );
  const showBannerPlaceholder = !bannerSrc;

  const open = restaurant.isOpen !== undefined ? restaurant.isOpen : restaurant.isAcceptingOrders;
  const activePromotions = restaurant.promotions?.filter((promo) => {
    const now = new Date();
    const start = new Date(promo.startDate);
    const end = new Date(promo.endDate);
    return promo.isActive && now >= start && now <= end;
  }) || [];

  const bestPromotion = activePromotions[0];
  return (
    <div
      onClick={onClick}
      className={cn(
        "relative bg-white rounded-2xl overflow-hidden cursor-pointer",
        "shadow-card hover:shadow-card-hover",
        "transition-all duration-300 hover:scale-105",
        "group",
        !open && "ring-1 ring-gray-200"
      )}
    >
      <div className="relative aspect-video bg-gradient-card overflow-hidden bg-gray-100">
        <div className={cn("absolute inset-0 group-hover:scale-105 transition-transform duration-500 ease-out", !open && "grayscale opacity-60")}>
          {showBannerPlaceholder ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200">
              <Icon name="image" size={48} className="text-gray-300" />
            </div>
          ) : (
            <img
              src={bannerSrc!}
              alt={restaurant.name}
              onError={() => {
                if (bannerSrc === restaurant.bannerUrl && restaurant.logoUrl) {
                  setBannerSrc(restaurant.logoUrl);
                } else {
                  setBannerSrc(null);
                }
              }}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          )}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />

        <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
          <div className={cn(
            "px-3 py-1.5 rounded-full backdrop-blur-md text-sm font-semibold shadow-soft",
            open ? "bg-success/90 text-white" : "bg-error/90 text-white"
          )}>
            {open ? '✅ Açık' : '⏰ Kapalı'}
          </div>

          {bestPromotion && (
            <div className={cn(
              "px-3 py-1.5 rounded-full backdrop-blur-md text-xs font-bold shadow-soft",
              "bg-primary text-white",
              "border-2 border-white/50",
              "transform transition-all duration-300 hover:scale-105"
            )}>
              🎉 {getPromotionBadgeText(bestPromotion)}
            </div>
          )}
        </div>

        <div className="absolute top-3 right-3 z-20">
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-md shadow-xl">
            <Icon name="star" size={16} className="text-warning fill-warning shrink-0" />
            <span className="font-bold text-gray-900 text-sm">{formatRating(restaurant.rating)}</span>
            {Number(restaurant.reviewCount) > 0 && (
              <span className="text-xs font-medium text-gray-500">
                ({Number(restaurant.reviewCount)})
              </span>
            )}
          </div>
        </div>

        {restaurant.logoUrl && (
          <div className="absolute bottom-3 right-3 w-12 h-12 rounded-xl bg-white shadow-premium border-2 border-white overflow-hidden z-20">
            <img
              src={restaurant.logoUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      <div className="p-5 bg-white">
        {!open && (
          <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-red-50 border border-red-100">
            <Icon name="clock" size={14} className="text-red-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-red-600">Bu restoran su anda kapali</span>
          </div>
        )}
        <h3 className={cn("font-bold text-xl mb-2 group-hover:text-primary transition-colors line-clamp-1", open ? "text-gray-900" : "text-gray-500")}>
          {restaurant.name}
        </h3>

        {restaurant.description && (
          <p className="text-sm text-gray-500 mb-4 line-clamp-2">
            {restaurant.description}
          </p>
        )}

        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Icon name="clock" size={18} className="text-primary flex-shrink-0" />
            <span className="font-semibold text-gray-700 text-sm">
              {restaurant.estimatedPreparationMinutes} dk
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Teslimat:</span>
            <span className="font-bold text-primary text-sm">{formatPrice(restaurant.deliveryFee || 0)}</span>
          </div>
        </div>

        {activePromotions.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {activePromotions.slice(0, 2).map((promo) => (
              <div
                key={promo.id}
                className={cn(
                  "px-2.5 py-1 rounded-lg text-xs font-semibold",
                  promo.type === 'FREE_DELIVERY'
                    ? "bg-success/10 text-success border border-success/30"
                    : "bg-primary/10 text-primary border border-primary/30"
                )}
              >
                {getPromotionBadgeText(promo)}
              </div>
            ))}
            {activePromotions.length > 2 && (
              <div className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                +{activePromotions.length - 2} kampanya
              </div>
            )}
          </div>
        )}

        {restaurant.minimumOrderAmount > 0 && (
          <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
            <Icon name="info" size={14} className="text-gray-400" />
            <span className="text-xs text-gray-500">Min. sipariş:</span>
            <span className="text-sm font-bold text-gray-900">{formatPrice(restaurant.minimumOrderAmount || 0)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantCard;
